/**
 * Member Resource Center — Alpine x-data factory (theme section).
 * Calls GET /apps/loyalty/member-resources (App Proxy); the Worker decides eligibility per post
 * (tier/role gate + mrc_eligible_customer_tags carve-out), so no client-side excluded check is needed.
 * Logged-in @oup.com / @k2.digital (and subdomains) staff skip that gate (Liquid sets cfg.staffBypass).
 */
(function () {
  "use strict";

  function mrcStaffBypass(cfg) {
    return Boolean(cfg && cfg.staffBypass === true);
  }

  function mrcStaffCatalog(cfg) {
    return cfg && Array.isArray(cfg.staffCatalog) ? cfg.staffCatalog : [];
  }

  /** Article slug only. Liquid `article.handle` can be `blog-handle/article-handle`; App Proxy sends the slug. */
  function mrcArticleHandleKey(h) {
    var s = String(h || "").trim();
    if (!s) return "";
    var i = s.lastIndexOf("/");
    return i === -1 ? s : s.slice(i + 1);
  }

  function mrcArticleCatalogItemMatchesCfg(cfg, item) {
    if (!item) return false;
    var wantKey = mrcArticleHandleKey(cfg.articleHandle);
    var itemKey = mrcArticleHandleKey(item.handle);
    return Boolean(wantKey && itemKey && wantKey === itemKey);
  }

  /** YouTube video id from a page or embed URL, or "". */
  function mrcYoutubeId(host, url) {
    var isYoutube =
      host === "youtube.com" ||
      host === "m.youtube.com" ||
      host === "youtube-nocookie.com" ||
      host === "youtu.be";
    if (!isYoutube) return "";
    var id = "";
    var parts = url.pathname.split("/").filter(Boolean);
    if (host === "youtu.be") {
      id = parts[0] || "";
    } else if (parts[0] === "embed" || parts[0] === "shorts") {
      id = parts[1] || "";
    } else if (url.pathname === "/watch" || url.pathname === "/watch/") {
      id = url.searchParams.get("v") || "";
    }
    if (!/^[A-Za-z0-9_-]{6,64}$/.test(id)) return "";
    return id;
  }

  /** Vimeo numeric id from a page or player URL, or "". */
  function mrcVimeoId(host, url) {
    if (host !== "vimeo.com" && host !== "player.vimeo.com") return "";
    var parts = url.pathname.split("/").filter(Boolean);
    var id = "";
    if (host === "player.vimeo.com") {
      var videoIdx = parts.indexOf("video");
      id = videoIdx >= 0 ? parts[videoIdx + 1] || "" : "";
    } else {
      for (var i = parts.length - 1; i >= 0; i--) {
        if (/^\d+$/.test(parts[i])) {
          id = parts[i];
          break;
        }
      }
    }
    if (!/^\d{5,12}$/.test(id)) return "";
    return id;
  }

  /** `media` is the Admin choice (labeled 短片). `video` is accepted as the same type. */
  function mrcIsVideoResourceType(resourceType) {
    var type = String(resourceType || "").toLowerCase();
    return type === "media" || type === "video";
  }

  function mrcEscapeAttr(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  /**
   * YouTube/Vimeo embed src, or "" when the type is not video/media, the URL is missing, or the host is not allowed.
   * Only those hosts are iframed.
   */
  function mrcVideoEmbedSrc(resourceType, videoUrl) {
    if (!mrcIsVideoResourceType(resourceType)) return "";
    var raw = String(videoUrl || "").trim();
    if (!raw) return "";
    var url;
    try {
      url = new URL(raw);
    } catch (e) {
      return "";
    }
    if (url.protocol !== "https:" && url.protocol !== "http:") return "";
    var host = url.hostname.replace(/^www\./i, "").toLowerCase();
    var youtubeId = mrcYoutubeId(host, url);
    if (youtubeId) {
      return "https://www.youtube-nocookie.com/embed/" + encodeURIComponent(youtubeId);
    }
    var vimeoId = mrcVimeoId(host, url);
    if (vimeoId) {
      return "https://player.vimeo.com/video/" + encodeURIComponent(vimeoId);
    }
    return "";
  }

  /** Embed src for a listing card, or "". */
  function mrcMediaVideoEmbedSrc(item) {
    if (!item) return "";
    return mrcVideoEmbedSrc(item.resource_type, item.video_url);
  }

  /** Player markup placed in front of an article body. Empty when there is nothing to embed. */
  function mrcArticleVideoPlayerHtml(resourceType, videoUrl, title) {
    var src = mrcVideoEmbedSrc(resourceType, videoUrl);
    if (!src) return "";
    return (
      '<div class="rewardly-mrc-article__player">' +
      '<iframe class="rewardly-mrc-article__player-frame" src="' +
      src +
      '" title="' +
      mrcEscapeAttr(title) +
      '" loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>' +
      "</div>"
    );
  }

  /** Fixed role-filter order. Any role not listed here is appended in API order. */
  var MRC_ROLE_ORDER = [
    "PARENT",
    "STUDENT",
    "BOOKLOVER",
    "LOCAL_TEACHER",
    "TEACHER_NONLOCAL_CUR",
    "LEARNINGCENTRE",
  ];

  function mrcRolesInFixedOrder(roles) {
    var list = Array.isArray(roles) ? roles.slice() : [];
    var rank = {};
    for (var i = 0; i < MRC_ROLE_ORDER.length; i++) {
      rank[MRC_ROLE_ORDER[i]] = i;
    }
    var tail = MRC_ROLE_ORDER.length;
    return list
      .map(function (role, index) {
        return { role: role, index: index };
      })
      .sort(function (a, b) {
        var ka = String(a.role == null ? "" : a.role).trim();
        var kb = String(b.role == null ? "" : b.role).trim();
        var ra = Object.prototype.hasOwnProperty.call(rank, ka) ? rank[ka] : tail;
        var rb = Object.prototype.hasOwnProperty.call(rank, kb) ? rank[kb] : tail;
        if (ra !== rb) return ra - rb;
        return a.index - b.index;
      })
      .map(function (entry) {
        return entry.role;
      });
  }

  function rewardlyMrcRoot(sectionId) {
    var cfg =
      typeof window !== "undefined"
        ? window["rewardlyMrcCfg_" + sectionId]
        : null;

    return {
      phase: "loading",
      errorMessage: "",
      items: [],
      allItems: [],
      customerMrcRoles: [],
      roleLens: "",
      resourceType: "",
      tagFilter: "",
      sortOrder: "newest",
      /** 'sort' | 'type' | 'role' | null */
      openDropdown: null,

      get filteredItems() {
        var list = this.allItems.slice();
        var rt = (this.resourceType || "").toLowerCase().trim();
        if (rt) {
          list = list.filter(function (item) {
            return (item.resource_type || "").toLowerCase() === rt;
          });
        }
        var th = (this.tagFilter || "").trim();
        if (th) {
          list = list.filter(function (item) {
            var tags = item.tags || [];
            return tags.some(function (t) {
              return t.handle === th;
            });
          });
        }
        var rl = (this.roleLens || "").trim();
        if (rl) {
          var self = this;
          list = list.filter(function (item) {
            return self.itemMatchesRoleLens(item, rl);
          });
        }
        var mult = this.sortOrder === "oldest" ? 1 : -1;
        list.sort(function (a, b) {
          var ta = new Date(a.published_at).getTime();
          var tb = new Date(b.published_at).getTime();
          if (isNaN(ta)) ta = 0;
          if (isNaN(tb)) tb = 0;
          return (ta - tb) * mult;
        });
        return list;
      },

      get tagFacets() {
        var counts = {};
        var labels = {};
        var map =
          cfg && cfg.tagLabels && typeof cfg.tagLabels === "object" && !Array.isArray(cfg.tagLabels)
            ? cfg.tagLabels
            : {};
        this.allItems.forEach(function (item) {
          (item.tags || []).forEach(function (t) {
            var h = t.handle;
            if (!h) return;
            counts[h] = (counts[h] || 0) + 1;
            labels[h] = map[h] || t.label || h;
          });
        });
        return Object.keys(counts)
          .map(function (handle) {
            return { handle: handle, label: labels[handle], count: counts[handle] };
          })
          .sort(function (a, b) {
            return a.label.localeCompare(b.label);
          });
      },

      init: function () {
        var self = this;
        if (!cfg) {
          this.phase = "error";
          this.errorMessage = "Config missing";
          return;
        }
        if (!cfg.customerId) {
          this.phase = "guest";
          return;
        }

        this.load()
          .then(function () {})
          .catch(function (e) {
            if (mrcStaffBypass(cfg)) {
              self.customerMrcRoles = [];
              self.allItems = mrcStaffCatalog(cfg);
              self.phase = "ready";
              return;
            }
            console.error("[Rewardly MRC]", e);
            self.phase = "error";
            self.errorMessage = (e && e.message) || "Load failed";
          });
      },

      load: async function () {
        var apiBase = (cfg.apiBase || "/apps/loyalty").replace(/\/$/, "");
        var locale = cfg.locale || "default";
        this.phase = "loading";
        this.errorMessage = "";
        this.customerMrcRoles = [];
        this.roleLens = "";
        this.openDropdown = null;

        // Excluded gating is decided server-side per post: excluded customers may still receive
        // tag-opened posts (mrc_eligible_customer_tags carve-out). Drive phase from the API response.
        var mrUrl =
          apiBase +
          "/member-resources?locale=" +
          encodeURIComponent(locale) +
          (cfg.catalogNoCache ? "&mrc_catalog_nocache=1" : "") +
          "&mrc_omit_body=1";
        var mrRes = await fetch(mrUrl);
        if (mrRes.status === 401) {
          this.phase = "guest";
          this.allItems = [];
          this.customerMrcRoles = [];
          return;
        }
        if (!mrRes.ok) {
          if (mrcStaffBypass(cfg)) {
            this.customerMrcRoles = [];
            this.allItems = mrcStaffCatalog(cfg);
            this.phase = "ready";
            return;
          }
          throw new Error("member-resources HTTP " + mrRes.status);
        }
        var data = await mrRes.json();
        if (!data.success) {
          if (mrcStaffBypass(cfg)) {
            this.customerMrcRoles = [];
            this.allItems = mrcStaffCatalog(cfg);
            this.phase = "ready";
            return;
          }
          throw new Error(data.error || "member-resources error");
        }

        if (mrcStaffBypass(cfg)) {
          this.customerMrcRoles = mrcRolesInFixedOrder(data.customer_mrc_roles);
          this.allItems = mrcStaffCatalog(cfg);
          if (!this.allItems.length) {
            this.allItems = data.items || [];
          }
          this.phase = "ready";
          return;
        }

        if (data.eligible === false) {
          this.phase = "excluded";
          this.allItems = [];
          this.customerMrcRoles = [];
          return;
        }

        this.customerMrcRoles = mrcRolesInFixedOrder(data.customer_mrc_roles);
        this.allItems = data.items || [];
        this.phase = "ready";
      },

      setTagFilter: function (handle) {
        this.tagFilter = this.tagFilter === handle ? "" : handle;
      },

      toggleDropdown: function (name) {
        this.openDropdown = this.openDropdown === name ? null : name;
      },

      closeDropdowns: function () {
        this.openDropdown = null;
      },

      pickSort: function (v) {
        this.sortOrder = v;
        this.closeDropdowns();
      },

      pickResourceType: function (v) {
        this.resourceType = v;
        this.closeDropdowns();
      },

      pickRole: function (v) {
        this.roleLens = v;
        this.closeDropdowns();
      },

      sortPillActive: function () {
        return (
          this.openDropdown === "sort" ||
          (this.sortOrder || "").toLowerCase() !== "newest"
        );
      },

      typePillActive: function () {
        return this.openDropdown === "type" || !!(this.resourceType || "").trim();
      },

      rolePillActive: function () {
        return this.openDropdown === "role" || !!(this.roleLens || "").trim();
      },

      tagDisplayLabel: function (tag) {
        var h = tag && tag.handle;
        var map =
          cfg && cfg.tagLabels && typeof cfg.tagLabels === "object" && !Array.isArray(cfg.tagLabels)
            ? cfg.tagLabels
            : {};
        var raw = (h && map[h]) || (tag && tag.label) || h || "";
        if (typeof raw === "string" && raw.indexOf("Translation missing:") === 0) {
          raw = (tag && tag.label) || h || "";
        }
        return raw;
      },

      oupRoleLabel: function (role) {
        var r = role && String(role).trim();
        if (!r) {
          return "";
        }
        var map =
          cfg && cfg.oupRoleLabels && typeof cfg.oupRoleLabels === "object" && !Array.isArray(cfg.oupRoleLabels)
            ? cfg.oupRoleLabels
            : {};
        var raw = map[r] || r;
        if (typeof raw === "string" && raw.indexOf("Translation missing:") === 0) {
          raw = r;
        }
        return raw;
      },

      formatPublishedDate: function (item) {
        var raw = item && item.published_at;
        if (!raw) {
          return "";
        }
        var d = new Date(raw);
        if (isNaN(d.getTime())) {
          return "";
        }
        var loc = (cfg && cfg.locale) || "en";
        var norm = String(loc).replace(/_/g, "-").toLowerCase();
        try {
          return new Intl.DateTimeFormat(norm, {
            year: "numeric",
            month: "long",
            day: "numeric",
          }).format(d);
        } catch (e) {
          return new Intl.DateTimeFormat("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          }).format(d);
        }
      },

      itemCardHref: function (item) {
        if (!item) {
          return "";
        }
        // Staff catalog: Liquid article.url is already a storefront-relative path.
        var u = String(item.article_url || "").trim();
        if (u.charAt(0) === "/" && u.charAt(1) !== "/") {
          return u;
        }
        // App Proxy items: listing base + handle (API handle is already the article slug).
        var baseRaw = cfg && cfg.articleListingBase != null ? String(cfg.articleListingBase).trim() : "";
        var handle = String(item.handle || "").trim();
        if (baseRaw && handle) {
          var base = baseRaw.replace(/\/+$/, "");
          return base + "/" + encodeURIComponent(handle);
        }
        if (u) {
          return u;
        }
        if ((item.resource_type || "").toLowerCase() === "media") {
          u = String(item.media_url || "").trim();
          if (u) {
            return u;
          }
        }
        return "";
      },

      itemCardOpensNewTab: function (item) {
        return !String(item.article_url || "").trim() && !!this.itemCardHref(item);
      },

      /** YouTube/Vimeo embed src for media cards, or "". */
      itemVideoEmbedSrc: function (item) {
        return mrcMediaVideoEmbedSrc(item);
      },

      /** Show the card thumbnail for image + article types when a featured image exists. */
      itemShowsThumbnail: function (item) {
        if (!item || !String(item.media_url || "").trim()) {
          return false;
        }
        var type = (item.resource_type || "").toLowerCase();
        return type === "image" || type === "article";
      },

      /** Client-side lens: article open to all roles, or empty eligible_roles, or contains roleKey. */
      itemMatchesRoleLens: function (item, roleKey) {
        if (!item || !roleKey) {
          return true;
        }
        if (item.show_all_roles === true) {
          return true;
        }
        var er = item.eligible_roles;
        if (!er || !er.length) {
          return true;
        }
        return er.indexOf(roleKey) !== -1;
      },

      formatMrcBool: function (v) {
        var dash = (cfg && cfg.dashLabel) || "—";
        if (v === true) {
          return "true";
        }
        if (v === false) {
          return "false";
        }
        return dash;
      },

      itemTierListDisplay: function (item) {
        var dash = (cfg && cfg.dashLabel) || "—";
        if (!item || !item.eligible_tiers || !item.eligible_tiers.length) {
          return dash;
        }
        return item.eligible_tiers.join(", ");
      },

      itemRoleListDisplay: function (item) {
        var dash = (cfg && cfg.dashLabel) || "—";
        if (!item || !item.eligible_roles || !item.eligible_roles.length) {
          return dash;
        }
        return item.eligible_roles.join(", ");
      },
    };
  }

  /**
   * Article template (`main-blog-post-mrc`): same §1 + tier/role gate as listing; show body only if
   * this post’s handle appears in `member-resources` items for the logged-in customer.
   */
  function rewardlyMrcArticleGate(sectionId) {
    var cfg =
      typeof window !== "undefined"
        ? window["rewardlyMrcArticleCfg_" + sectionId]
        : null;

    return {
      phase: "loading",
      errorMessage: "",
      matchedHtml: "",

      init: function () {
        var self = this;
        if (!cfg) {
          this.phase = "error";
          this.errorMessage = "Config missing";
          return;
        }
        if (!cfg.customerId) {
          this.phase = "guest";
          return;
        }
        if (mrcStaffBypass(cfg)) {
          this.phase = "allowed";
          return;
        }

        this.verifyAccess()
          .then(function () {})
          .catch(function (e) {
            console.error("[Rewardly MRC article]", e);
            self.phase = "error";
            self.errorMessage = (e && e.message) || "Load failed";
          });
      },

      verifyAccess: async function () {
        var apiBase = (cfg.apiBase || "/apps/loyalty").replace(/\/$/, "");
        var locale = cfg.locale || "default";
        this.phase = "loading";
        this.errorMessage = "";

        // Excluded gating is decided server-side per post (tag carve-out); rely on the API response
        // and on whether this article's handle appears in the returned items.
        var mrUrl =
          apiBase +
          "/member-resources?locale=" +
          encodeURIComponent(locale) +
          (cfg.catalogNoCache ? "&mrc_catalog_nocache=1" : "");
        var mrRes = await fetch(mrUrl);
        if (mrRes.status === 401) {
          this.matchedHtml = "";
          this.phase = "guest";
          return;
        }
        if (!mrRes.ok) {
          throw new Error("member-resources HTTP " + mrRes.status);
        }
        var data = await mrRes.json();
        if (!data.success) {
          throw new Error(data.error || "member-resources error");
        }

        if (data.eligible === false) {
          this.matchedHtml = "";
          this.phase = "excluded";
          return;
        }

        var items = data.items || [];
        var matched = null;
        for (var i = 0; i < items.length; i++) {
          if (mrcArticleCatalogItemMatchesCfg(cfg, items[i])) {
            matched = items[i];
            break;
          }
        }
        if (matched) {
          var body = matched.body != null ? String(matched.body) : "";
          var player = mrcArticleVideoPlayerHtml(
            matched.resource_type,
            matched.video_url,
            matched.title
          );
          this.matchedHtml = player + body;
          this.phase = "allowed";
        } else {
          this.matchedHtml = "";
          this.phase = "denied";
        }
      },
    };
  }

  window.rewardlyMrcRoot = rewardlyMrcRoot;
  window.rewardlyMrcArticleGate = rewardlyMrcArticleGate;
  window.rewardlyMrcArticleVideoHtml = mrcArticleVideoPlayerHtml;
})();
